/* ---------------------------------------------------------------------------
   Weekly Schedule engine
   ---------------------------------------------------------------------------
   Pure helpers shared by the Weekly Schedule page, its live preview, the
   Settings panel and the public display (Display.vue). The API stores rules
   (see the backend's interfaces/IWeekly_Schedule_N); everything time-shaped
   — which items fall on a day, the clock time a zman-relative item lands on,
   what is on now and next, how a day header reads — is worked out here, so
   the admin grid and the screen can never disagree.

   Days are wall-clock "YYYY-MM-DD" strings and times are minutes past
   midnight in the location's timezone.
   -------------------------------------------------------------------------- */
import { HDate, HebrewCalendar, Sedra, flags } from '@hebcal/core';
import {
  buildPreviewZmanimContext,
  PreviewZmanimContext,
  resolveMinyanZmanIso,
  zmanIsoToMinutesOfDay,
} from '@/utils/minyan_roll_preview_zmanim';
import { getKosherZmanimOptionLabel } from '@/utils/kosher_zmanim_options';
import { toWallClockDate, toWallClockDateInputValue } from '@/utils/wall_clock_date';
import { BANNER_SHABBOS_LABEL, BANNER_WEEKDAY_NAMES, DAYS_IN_HEBREW, MONTHS_IN_HEBREW } from '@/utils/const';
import { formatHebrewYear } from '@/utils/hebrew_year';
import { markAuthoredBlankLines } from '@/utils/authored_blank_lines';
import { clampColumnCount } from '@/utils/column_layout';

/* ------------------------------- vocabulary ------------------------------ */

/**
 * The artboard feature flag that puts the board on an artboard — ticked under
 * Content in the admin's screen designer, like every other kind of content.
 * The Display tab of the Weekly Schedule page ticks it on the one screen and
 * artboard picked there, which shows the complete schedule. Routing (rooms,
 * kinds, date header, layout) is still honoured where an earlier setup saved
 * some, until that tab is saved again.
 */
export const WEEKLY_SCHEDULE_FEATURE_N = 'WeeklySchedule_N';

export type WeeklyScheduleKind = 'minyan' | 'shiur' | 'event' | 'other';
export type WeeklyScheduleHeaderPreset = 'compact' | 'standard' | 'full';
export type WeeklyScheduleLayout = 'day' | 'week';

export const WEEKLY_SCHEDULE_KINDS: Array<{ value: WeeklyScheduleKind; label: string; plural: string }> = [
  { value: 'minyan', label: 'Minyan', plural: 'Minyanim' },
  { value: 'shiur', label: 'Shiur', plural: 'Shiurim' },
  { value: 'event', label: 'Event', plural: 'Events' },
  { value: 'other', label: 'Other', plural: 'Other' },
];

export const WEEKLY_SCHEDULE_HEADER_PRESETS: Array<{
  value: WeeklyScheduleHeaderPreset;
  label: string;
  example: string;
}> = [
  { value: 'compact', label: 'Compact', example: 'FRIDAY' },
  { value: 'standard', label: 'Standard', example: 'FRIDAY, SEP 12' },
  { value: 'full', label: 'Full', example: 'FRIDAY, SEP 12 | 22 ELUL 5786' },
];

export const WEEKLY_SCHEDULE_LAYOUTS: Array<{ value: WeeklyScheduleLayout; label: string; hint: string }> = [
  { value: 'day', label: 'Today', hint: 'One day at a time, with the live Now & Next guide.' },
  { value: 'week', label: 'Week', hint: 'Sunday through Shabbat, as many days to a row as Columns sets.' },
];

// Sunday first: the Jewish week, with Shabbat as its last column.
export const WEEKLY_SCHEDULE_DAYS: Array<{ value: number; label: string; short: string }> = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Shabbat', short: 'Shabbat' },
];

export const WEEKLY_SCHEDULE_DAY_PRESETS: Array<{ key: string; label: string; days: number[] }> = [
  { key: 'sun-thu', label: 'Sun – Thu', days: [0, 1, 2, 3, 4] },
  { key: 'sun-fri', label: 'Sun – Fri', days: [0, 1, 2, 3, 4, 5] },
  { key: 'every', label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
  { key: 'shabbat', label: 'Shabbat', days: [6] },
];

export type WeeklyScheduleCardMode = 'single' | 'separate';

/**
 * The board's look. The words are authored in the Design tab's rich-text
 * editor as two templates — the heading printed once at the top of each day,
 * and the event repeated for every item — and the rest arranges them: every
 * event inside one shared card, or a card of its own in a grid of columns.
 */
export interface WeeklyScheduleDesign {
  headingHtml: string;
  eventHtml: string;
  cardMode: WeeklyScheduleCardMode;
  /** Cards (or rows of the shared card) per row of the Today layout. */
  columns: number;
  /** Days per row of the Week layout, Sunday first; each day stacks its events. */
  weekColumns: number;
  /** "Vpx Hpx" — inside each card. */
  cardPadding: string;
  /** Between cards, or between the rows of the shared card. */
  cardSpacing: number;
  backgroundColor: string;
  cardBackgroundColor: string;
  cardBorderColor: string;
  /** The line between rows of the shared card. */
  dividerColor: string;
  /** Marks what is on now and what is next. */
  highlightColor: string;
  layout: WeeklyScheduleLayout;
}

// The starting templates. Each variable sits on its own line, so one with
// nothing to say for a day (no parsha, no day notes) folds away instead of
// leaving a gap.
export const DEFAULT_WEEKLY_SCHEDULE_HEADING_HTML = [
  '<p><span style="font-size: 44px"><strong>{dayOfWeek}, {monthDay}</strong></span></p>',
  '<p><span style="font-size: 22px; color: #6b7280">{hebrewDayOfWeek} {hebrewMonthDay} {hebrewYear}</span></p>',
  '<p><span style="font-size: 22px; color: #6b7280">{parsha}</span></p>',
  '<p><span style="font-size: 22px; color: #6b7280">{holidays}</span></p>',
  '<p><span style="font-size: 22px; color: #6366f1"><strong>{countdown}</strong></span></p>',
  '<p><span style="font-size: 20px; color: #6b7280"><em>{dayNotes}</em></span></p>',
].join('');

export const DEFAULT_WEEKLY_SCHEDULE_EVENT_HTML = [
  '<p data-split="true" style="display: flex; align-items: center; justify-content: space-between; gap: 12px">',
  '<span style="font-size: 28px"><strong>{title}</strong></span>',
  '<span style="font-size: 28px; color: #6366f1"><strong>{time}</strong></span>',
  '</p>',
  '<p><span style="font-size: 20px; color: #6b7280">{room}</span></p>',
  '<p><span style="font-size: 18px; color: #6b7280">{notes}</span></p>',
].join('');

export const DEFAULT_WEEKLY_SCHEDULE_DESIGN: WeeklyScheduleDesign = {
  headingHtml: DEFAULT_WEEKLY_SCHEDULE_HEADING_HTML,
  eventHtml: DEFAULT_WEEKLY_SCHEDULE_EVENT_HTML,
  cardMode: 'separate',
  columns: 1,
  weekColumns: 7,
  cardPadding: '16px 20px',
  cardSpacing: 12,
  backgroundColor: '#ffffff',
  cardBackgroundColor: '#f8fafc',
  cardBorderColor: '#e2e8f0',
  dividerColor: '#e2e8f0',
  highlightColor: '#6366f1',
  layout: 'day',
};

export const WEEKLY_SCHEDULE_CARD_MODES: Array<{ value: WeeklyScheduleCardMode; label: string; hint: string }> = [
  { value: 'single', label: 'One Card', hint: 'Every event inside one big card.' },
  { value: 'separate', label: 'Separate Cards', hint: 'Each event in a card of its own.' },
];

export const WEEKLY_SCHEDULE_CARD_PADDING_MAX = 120;
export const WEEKLY_SCHEDULE_CARD_SPACING_MAX = 80;
// Every day of the week side by side.
export const WEEKLY_SCHEDULE_WEEK_COLUMNS_MAX = WEEKLY_SCHEDULE_DAYS.length;

export interface WeeklyScheduleSettings {
  headerPreset: WeeklyScheduleHeaderPreset;
  showNowNext: boolean;
  showCountdown: boolean;
  holdUnverified: boolean;
  hidePastItems: boolean;
  displayDurationSeconds: number;
  design: Partial<WeeklyScheduleDesign> | null;
}

export const DEFAULT_WEEKLY_SCHEDULE_SETTINGS: WeeklyScheduleSettings = {
  headerPreset: 'standard',
  showNowNext: true,
  showCountdown: false,
  holdUnverified: true,
  hidePastItems: false,
  displayDurationSeconds: 60,
  design: null,
};

const HEX_RE = /^#[0-9a-f]{6}$/i;
const CSS_COLOR_RE = /^(#[0-9a-f]{3}|#[0-9a-f]{4}|#[0-9a-f]{6}|#[0-9a-f]{8}|transparent|rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(,\s*[\d.]+%?\s*)?\))$/i;

const asBool = (value: unknown, fallback: boolean): boolean => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return fallback;
};

const asNumber = (value: unknown, fallback: number, min: number, max: number): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(Math.round(n), min), max);
};

/** A colour a screen can paint: a hex, rgb()/rgba() or `transparent`; else the fallback. */
export const asScheduleColor = (value: unknown, fallback: string): string => {
  const color = typeof value === 'string' ? value.trim() : '';
  return color && CSS_COLOR_RE.test(color) ? color : fallback;
};

export const isTransparentScheduleColor = (value: unknown): boolean =>
  String(value || '').trim().toLowerCase() === 'transparent';

/** A colour the native picker can show: the value when it is a 6-digit hex, else the fallback. */
export const scheduleColorPickerValue = (value: unknown, fallback = '#ffffff'): string => {
  const color = String(value || '').trim();
  return HEX_RE.test(color) ? color : fallback;
};

/** `rgba()` of a hex at the given opacity, or '' when the colour is not a hex (or is transparent). */
export function scheduleColorWithAlpha(value: unknown, alpha: number): string {
  let hex = String(value || '').trim().replace('#', '');
  if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return '';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
}

export interface ScheduleCardPadding {
  vertical: number;
  horizontal: number;
}

/** "16px 20px" → { vertical: 16, horizontal: 20 }; one value covers both; anything else → the default. */
export function parseScheduleCardPadding(value: unknown): ScheduleCardPadding {
  const fallback = { vertical: 16, horizontal: 20 };
  const tokens = String(value ?? '').trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
  if (!tokens.length || tokens.length > 2) return fallback;
  const numbers = tokens.map((token) => {
    const match = token.match(/^(\d+(?:\.\d+)?)(px)?$/i);
    return match ? Math.min(Math.max(Math.round(Number(match[1])), 0), WEEKLY_SCHEDULE_CARD_PADDING_MAX) : null;
  });
  if (numbers.some((n) => n === null)) return fallback;
  return { vertical: numbers[0] as number, horizontal: (numbers[1] ?? numbers[0]) as number };
}

export const formatScheduleCardPadding = (padding: ScheduleCardPadding): string =>
  `${padding.vertical}px ${padding.horizontal}px`;

const asTemplate = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() ? value : fallback;

/** A complete design, with every missing or broken field on its default. */
export function normalizeWeeklyScheduleDesign(source: unknown): WeeklyScheduleDesign {
  const raw = (source && typeof source === 'object' ? source : {}) as Record<string, unknown>;
  const d = DEFAULT_WEEKLY_SCHEDULE_DESIGN;
  return {
    headingHtml: asTemplate(raw.headingHtml, d.headingHtml),
    eventHtml: asTemplate(raw.eventHtml, d.eventHtml),
    cardMode: raw.cardMode === 'single' ? 'single' : 'separate',
    columns: clampColumnCount(raw.columns, d.columns),
    weekColumns: asNumber(raw.weekColumns ?? d.weekColumns, d.weekColumns, 1, WEEKLY_SCHEDULE_WEEK_COLUMNS_MAX),
    cardPadding: formatScheduleCardPadding(parseScheduleCardPadding(raw.cardPadding ?? d.cardPadding)),
    cardSpacing: asNumber(raw.cardSpacing, d.cardSpacing, 0, WEEKLY_SCHEDULE_CARD_SPACING_MAX),
    backgroundColor: asScheduleColor(raw.backgroundColor, d.backgroundColor),
    cardBackgroundColor: asScheduleColor(raw.cardBackgroundColor, d.cardBackgroundColor),
    cardBorderColor: asScheduleColor(raw.cardBorderColor, d.cardBorderColor),
    dividerColor: asScheduleColor(raw.dividerColor, d.dividerColor),
    highlightColor: asScheduleColor(raw.highlightColor, d.highlightColor),
    layout: raw.layout === 'week' ? 'week' : 'day',
  };
}

export function normalizeWeeklyScheduleSettings(source: unknown): WeeklyScheduleSettings {
  const raw = (source && typeof source === 'object' ? source : {}) as Record<string, unknown>;
  const d = DEFAULT_WEEKLY_SCHEDULE_SETTINGS;
  const preset = String(raw.headerPreset || '');
  return {
    headerPreset: (['compact', 'standard', 'full'].includes(preset) ? preset : d.headerPreset) as WeeklyScheduleHeaderPreset,
    showNowNext: asBool(raw.showNowNext, d.showNowNext),
    showCountdown: asBool(raw.showCountdown, d.showCountdown),
    holdUnverified: asBool(raw.holdUnverified, d.holdUnverified),
    hidePastItems: asBool(raw.hidePastItems, d.hidePastItems),
    displayDurationSeconds: asNumber(raw.displayDurationSeconds, d.displayDurationSeconds, 1, 600),
    design: raw.design && typeof raw.design === 'object' ? (raw.design as Partial<WeeklyScheduleDesign>) : null,
  };
}

/* --------------------------------- dates --------------------------------- */

const pad2 = (n: number) => String(n).padStart(2, '0');

/** "YYYY-MM-DD" of a local Date. */
export const isoOfDate = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** Local noon of an ISO day: safe from DST and midnight ambiguity. */
export const dateOfIso = (iso: string): Date => {
  const date = toWallClockDate(iso);
  if (!date) return new Date(NaN);
  date.setHours(12, 0, 0, 0);
  return date;
};

export const shiftIso = (iso: string, days: number): string => {
  const date = dateOfIso(iso);
  date.setDate(date.getDate() + days);
  return isoOfDate(date);
};

/** The day it is right now in a timezone ("YYYY-MM-DD"). */
export function todayIsoInTimeZone(timeZone?: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
    const year = get('year');
    const month = get('month');
    const day = get('day');
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch (e) {
    /* fall through */
  }
  return isoOfDate(now);
}

/** Minutes past midnight right now in a timezone. */
export function minutesOfDayInTimeZone(timeZone?: string, now: Date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    if (Number.isFinite(hour) && Number.isFinite(minute)) return (hour % 24) * 60 + minute;
  } catch (e) {
    /* fall through */
  }
  return now.getHours() * 60 + now.getMinutes();
}

/* ----------------------------- Jewish context ---------------------------- */

export interface ScheduleDayMarker {
  key: string;
  label: string;
  tone: 'shabbat' | 'holiday' | 'rosh-chodesh' | 'fast' | 'parsha' | 'note';
}

export interface ScheduleDay {
  iso: string;
  date: Date;
  dayOfWeek: number;
  dayLabel: string;
  dayShort: string;
  /** "Sep 12" */
  monthDay: string;
  /** "September 12" */
  monthDayLong: string;
  year: number;
  /** "Friday, Sep 12" */
  longLabel: string;
  dayNumber: number;
  /** "22 Elul" (transliterated) */
  hebrewLabel: string;
  /** "22 Elul 5786" */
  hebrewFull: string;
  /** 'כ"ב אלול' */
  hebrewLabelHe: string;
  /** 'כ"ב אלול תשפ"ו' */
  hebrewFullHe: string;
  /** 'תשפ"ו' */
  hebrewYearHe: string;
  /** 'יום שישי', and 'שבת קודש' on Shabbat */
  hebrewWeekdayHe: string;
  parsha: string;
  holidays: string[];
  isShabbat: boolean;
  isRoshChodesh: boolean;
  isFastDay: boolean;
  isYomTov: boolean;
  /** Anything a shul would call a special day: holiday, fast, Rosh Chodesh, Chol HaMoed… */
  isSpecial: boolean;
  isToday: boolean;
  markers: ScheduleDayMarker[];
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Each weekday as a Hebrew date line names it, Sunday first.
const HEBREW_WEEKDAYS = [...BANNER_WEEKDAY_NAMES, 'יום שישי', BANNER_SHABBOS_LABEL];

const sedraCache = new Map<number, Sedra>();
const sedraFor = (hebrewYear: number): Sedra => {
  let sedra = sedraCache.get(hebrewYear);
  if (!sedra) {
    sedra = new Sedra(hebrewYear, false);
    sedraCache.set(hebrewYear, sedra);
  }
  return sedra;
};

// The parsha read on the Shabbat that closes this day's week.
const parshaOfWeek = (date: Date): string => {
  try {
    const saturday = new Date(date);
    saturday.setDate(date.getDate() + (6 - date.getDay()));
    const hd = new HDate(saturday);
    const sedra = sedraFor(hd.getFullYear());
    const names = sedra.get(hd);
    return Array.isArray(names) ? names.join('-') : '';
  } catch (e) {
    return '';
  }
};

const holidayEventsOf = (hd: HDate): any[] => {
  try {
    return (HebrewCalendar.getHolidaysOnDate(hd, false) || []) as any[];
  } catch (e) {
    return [];
  }
};

const transliteratedHebrewDate = (hd: HDate): { label: string; full: string } => {
  const monthName = (hd as any).getMonthName ? String((hd as any).getMonthName()) : '';
  const day = hd.getDate();
  return {
    label: `${day} ${monthName}`.trim(),
    full: `${day} ${monthName} ${hd.getFullYear()}`.trim(),
  };
};

const hebrewLetterDate = (hd: HDate): string => {
  const day = DAYS_IN_HEBREW[hd.getDate() - 1] || String(hd.getDate());
  const month = hd.getMonth();
  const monthHe = month >= 1 && month <= 11
    ? MONTHS_IN_HEBREW[month - 1]
    : month === 13 ? MONTHS_IN_HEBREW[13] : (HDate.monthsInYear(hd.getFullYear()) === 13 ? MONTHS_IN_HEBREW[12] : MONTHS_IN_HEBREW[11]);
  return `${day} ${monthHe}`;
};

/** Everything the headers and markers need to know about one civil day. */
export function buildScheduleDay(isoLike: string | Date, todayIso: string = isoOfDate(new Date())): ScheduleDay {
  const date = isoLike instanceof Date ? new Date(isoLike) : dateOfIso(isoLike);
  date.setHours(12, 0, 0, 0);
  const iso = isoOfDate(date);
  const dayOfWeek = date.getDay();
  const dayMeta = WEEKLY_SCHEDULE_DAYS[dayOfWeek];
  const monthDay = `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;

  let hebrewLabel = '';
  let hebrewFull = '';
  let hebrewLabelHe = '';
  let hebrewFullHe = '';
  let hebrewYearHe = '';
  let parsha = '';
  const holidays: string[] = [];
  let isRoshChodesh = false;
  let isFastDay = false;
  let isYomTov = false;
  let isSpecial = false;

  try {
    const hd = new HDate(date);
    const transliterated = transliteratedHebrewDate(hd);
    hebrewLabel = transliterated.label;
    hebrewFull = transliterated.full;
    hebrewLabelHe = hebrewLetterDate(hd);
    hebrewYearHe = formatHebrewYear(hd.getFullYear());
    hebrewFullHe = `${hebrewLabelHe} ${hebrewYearHe}`;
    parsha = parshaOfWeek(date);

    for (const event of holidayEventsOf(hd)) {
      const mask = typeof event.getFlags === 'function' ? Number(event.getFlags()) : 0;
      if (mask & flags.SHABBAT_MEVARCHIM) continue;
      if (mask & flags.PARSHA_HASHAVUA) continue;
      if (mask & flags.ROSH_CHODESH) {
        isRoshChodesh = true;
        isSpecial = true;
        continue;
      }
      if (mask & (flags.MAJOR_FAST | flags.MINOR_FAST)) {
        isFastDay = true;
        isSpecial = true;
      }
      if (mask & flags.CHAG) isYomTov = true;
      if (mask & (flags.CHAG | flags.MINOR_HOLIDAY | flags.CHOL_HAMOED | flags.EREV | flags.MAJOR_FAST | flags.MINOR_FAST | flags.MODERN_HOLIDAY | flags.SPECIAL_SHABBAT)) {
        isSpecial = true;
        const name = typeof event.render === 'function' ? String(event.render('en')) : String(event.getDesc?.() || '');
        if (name && !holidays.includes(name)) holidays.push(name);
      }
    }
  } catch (e) {
    /* a day the calendar cannot place still gets its civil labels */
  }

  const markers: ScheduleDayMarker[] = [];
  if (dayOfWeek === 6) markers.push({ key: 'shabbat', label: 'Shabbat', tone: 'shabbat' });
  if (isRoshChodesh) markers.push({ key: 'rosh-chodesh', label: 'Rosh Chodesh', tone: 'rosh-chodesh' });
  holidays.forEach((name) => markers.push({
    key: `holiday-${name}`,
    label: name,
    tone: isFastDay && /fast|tzom|ta'?anit|tisha/i.test(name) ? 'fast' : 'holiday',
  }));
  if (parsha) markers.push({ key: 'parsha', label: `Parshat ${parsha}`, tone: 'parsha' });

  return {
    iso,
    date,
    dayOfWeek,
    dayLabel: dayMeta.label,
    dayShort: dayMeta.short,
    monthDay,
    monthDayLong: `${LONG_MONTHS[date.getMonth()]} ${date.getDate()}`,
    year: date.getFullYear(),
    longLabel: `${dayMeta.label}, ${monthDay}`,
    dayNumber: date.getDate(),
    hebrewLabel,
    hebrewFull,
    hebrewLabelHe,
    hebrewFullHe,
    hebrewYearHe,
    hebrewWeekdayHe: HEBREW_WEEKDAYS[dayOfWeek] || '',
    parsha,
    holidays,
    isShabbat: dayOfWeek === 6,
    isRoshChodesh,
    isFastDay,
    isYomTov,
    isSpecial,
    isToday: iso === todayIso,
    markers,
  };
}

/** True when a date is a holiday, fast, Rosh Chodesh or Chol HaMoed — a date whose schedule a second admin should check. */
export function isSpecialScheduleDate(iso: string): boolean {
  return buildScheduleDay(iso).isSpecial;
}

/** The Sunday-to-Shabbat week that holds `anchorIso`. */
export function buildScheduleWeek(anchorIso: string, todayIso?: string): ScheduleDay[] {
  const anchor = dateOfIso(anchorIso);
  if (Number.isNaN(anchor.getTime())) return buildScheduleWeek(isoOfDate(new Date()), todayIso);
  const sunday = new Date(anchor);
  sunday.setDate(anchor.getDate() - anchor.getDay());
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(sunday);
    date.setDate(sunday.getDate() + index);
    return buildScheduleDay(date, todayIso);
  });
}

/** "September 09 – 15, 2026", or "Sep 28 – Oct 4, 2026" across a month end. */
export function weekRangeLabel(week: ScheduleDay[]): string {
  if (!week.length) return '';
  const first = week[0].date;
  const last = week[week.length - 1].date;
  if (first.getMonth() === last.getMonth()) {
    return `${LONG_MONTHS[first.getMonth()]} ${pad2(first.getDate())} – ${pad2(last.getDate())}, ${last.getFullYear()}`;
  }
  const firstYear = first.getFullYear() === last.getFullYear() ? '' : `, ${first.getFullYear()}`;
  return `${SHORT_MONTHS[first.getMonth()]} ${first.getDate()}${firstYear} – ${SHORT_MONTHS[last.getMonth()]} ${last.getDate()}, ${last.getFullYear()}`;
}

/** The public-screen date header in the chosen preset. */
export function formatScheduleHeader(day: ScheduleDay, preset: WeeklyScheduleHeaderPreset): string {
  const dayName = day.dayLabel.toUpperCase();
  if (preset === 'compact') return dayName;
  const civil = `${dayName}, ${day.monthDay.toUpperCase()}`;
  if (preset === 'standard') return civil;
  return day.hebrewFull ? `${civil} | ${day.hebrewFull.toUpperCase()}` : civil;
}

/* ---------------------------------- time --------------------------------- */

export const clockToMinutes = (clock: unknown): number | null => {
  if (typeof clock !== 'string') return null;
  const match = clock.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
};

export const minutesToClock = (minutes: number): string => {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

/** "7:30 AM" / "7:30 PM". */
export function formatMinutes(minutes: number | null | undefined, options: { withPeriod?: boolean } = {}): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '--:--';
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const hour24 = Math.floor(total / 60);
  const minute = total % 60;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  return options.withPeriod === false ? `${hour12}:${pad2(minute)}` : `${hour12}:${pad2(minute)} ${period}`;
}

/** "9:15 – 9:45 PM" / "11:45 AM – 12:30 PM". */
export function formatMinutesRange(start: number | null | undefined, end: number | null | undefined): string {
  if (start === null || start === undefined || !Number.isFinite(start)) return '--:--';
  if (end === null || end === undefined || !Number.isFinite(end) || end <= start) return formatMinutes(start);
  const samePeriod = (start >= 720) === (end >= 720) && end < 1440;
  return samePeriod
    ? `${formatMinutes(start, { withPeriod: false })} – ${formatMinutes(end)}`
    : `${formatMinutes(start)} – ${formatMinutes(end)}`;
}

export function formatOffsetMinutes(offset: number): string {
  const n = Math.round(Number(offset) || 0);
  if (!n) return '';
  return `${n > 0 ? '+' : '−'} ${Math.abs(n)} min`;
}

/** "Sunset − 15 min", "At Tzeis", "7:30 AM". */
export function formatTimingSummary(timing: {
  timingMode?: string | null;
  fixedTime?: string | null;
  zmanKey?: string | null;
  zmanOffsetMinutes?: number | null;
}): string {
  if (timing.timingMode === 'zman') {
    const label = getKosherZmanimOptionLabel(String(timing.zmanKey || '')) || 'Zman';
    const offset = formatOffsetMinutes(Number(timing.zmanOffsetMinutes) || 0);
    return offset ? `${label} ${offset}` : `At ${label}`;
  }
  const minutes = clockToMinutes(timing.fixedTime);
  return minutes === null ? 'Time not set' : formatMinutes(minutes);
}

/* --------------------------------- zmanim -------------------------------- */

export interface ScheduleLocation {
  latitude?: number | string | null;
  longitude?: number | string | null;
  timezone?: string | null;
  name?: string | null;
}

export interface ResolvedZman {
  iso: string;
  minutes: number;
}

/**
 * Resolves a zman + offset to a clock time for any day, from the location's
 * coordinates, caching per day and zman. `available` is false when the
 * location has no usable coordinates — zman-relative items then show as
 * unresolved rather than at a made-up time.
 */
export class ScheduleZmanResolver {
  readonly timeZone: string;
  readonly available: boolean;
  private readonly ctx: PreviewZmanimContext | null;
  private readonly cache = new Map<string, ResolvedZman | null>();

  constructor(ctx: PreviewZmanimContext | null, fallbackTimeZone?: string) {
    this.ctx = ctx;
    this.available = Boolean(ctx?.calendar);
    this.timeZone = ctx?.timeZone
      || (typeof fallbackTimeZone === 'string' && fallbackTimeZone.trim())
      || Intl.DateTimeFormat().resolvedOptions().timeZone
      || 'UTC';
  }

  resolve(dateIso: string, zmanKey: string, offsetMinutes = 0): ResolvedZman | null {
    if (!this.ctx?.calendar || !zmanKey || !dateIso) return null;
    const key = `${dateIso}|${zmanKey}|${Math.round(Number(offsetMinutes) || 0)}`;
    if (this.cache.has(key)) return this.cache.get(key) || null;

    const calendar = this.ctx.calendar;
    let original: any = null;
    try {
      original = calendar.getDate();
    } catch (e) {
      original = null;
    }

    let result: ResolvedZman | null = null;
    try {
      calendar.setDate(dateOfIso(dateIso));
      const iso = resolveMinyanZmanIso(this.ctx, zmanKey, Number(offsetMinutes) || 0);
      if (iso) {
        const minutes = zmanIsoToMinutesOfDay(iso, this.timeZone);
        if (Number.isFinite(minutes)) result = { iso, minutes };
      }
    } catch (e) {
      result = null;
    } finally {
      if (original != null) {
        try {
          calendar.setDate(original);
        } catch (e) {
          /* noop */
        }
      }
    }

    this.cache.set(key, result);
    return result;
  }
}

/** Build a resolver for a location; one that resolves nothing when the location is unknown. */
export async function createScheduleZmanResolver(
  location: ScheduleLocation | null | undefined,
  specialZmanimOverrides?: any[] | null
): Promise<ScheduleZmanResolver> {
  const ctx = await buildPreviewZmanimContext(location || null, specialZmanimOverrides || []);
  return new ScheduleZmanResolver(ctx, location?.timezone || undefined);
}

/* -------------------------------- occurrences ---------------------------- */

export interface ScheduleItemLike {
  id: string;
  title: string;
  kind?: string | null;
  recurrenceType?: string | null;
  daysOfWeek?: number[] | null;
  date?: string | null;
  timingMode?: string | null;
  fixedTime?: string | null;
  zmanKey?: string | null;
  zmanOffsetMinutes?: number | null;
  durationMinutes?: number | null;
  roomId?: string | null;
  notes?: string | null;
  color?: string | null;
  needsVerification?: boolean;
  order?: number | null;
  templateId?: string | null;
  isActive?: boolean;
  isDeleted?: boolean;
  overrides?: ScheduleOverrideLike[] | null;
}

export interface ScheduleOverrideLike {
  id: string;
  itemId: string;
  date: string;
  action?: string | null;
  timingMode?: string | null;
  fixedTime?: string | null;
  zmanKey?: string | null;
  zmanOffsetMinutes?: number | null;
  durationMinutes?: number | null;
  roomId?: string | null;
  note?: string | null;
  needsVerification?: boolean;
  isDeleted?: boolean;
}

export interface ScheduleOccurrence {
  key: string;
  item: ScheduleItemLike;
  override: ScheduleOverrideLike | null;
  date: string;
  dayOfWeek: number;
  title: string;
  kind: WeeklyScheduleKind;
  roomId: string | null;
  roomName: string;
  startMinutes: number | null;
  endMinutes: number | null;
  durationMinutes: number;
  timeLabel: string;
  rangeLabel: string;
  timingSummary: string;
  isZman: boolean;
  zmanUnresolved: boolean;
  recurrenceLabel: string;
  isOneOff: boolean;
  isOverridden: boolean;
  isCancelled: boolean;
  needsVerification: boolean;
  notes: string;
  overrideNote: string;
  color: string | null;
}

export const kindOf = (value: unknown): WeeklyScheduleKind =>
  (['minyan', 'shiur', 'event', 'other'].includes(String(value)) ? String(value) : 'other') as WeeklyScheduleKind;

export function kindLabel(kind: unknown): string {
  return WEEKLY_SCHEDULE_KINDS.find((k) => k.value === kindOf(kind))?.label || 'Other';
}

const DAY_SHORTS = WEEKLY_SCHEDULE_DAYS.map((d) => d.short);

/** "Every Sunday", "Sun – Thu", "Sun, Tue & Thu", "Every day", "Sep 12 only". */
export function recurrenceLabel(item: ScheduleItemLike): string {
  if (item.recurrenceType === 'once') {
    const date = item.date ? dateOfIso(item.date) : null;
    return date && !Number.isNaN(date.getTime())
      ? `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()} only`
      : 'One date';
  }
  const days = [...new Set((item.daysOfWeek || []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b);
  if (!days.length) return 'No days';
  if (days.length === 7) return 'Every day';
  if (days.length === 1) return `Every ${WEEKLY_SCHEDULE_DAYS[days[0]].label}`;
  const contiguous = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  if (contiguous && days.length >= 3) return `${DAY_SHORTS[days[0]]} – ${DAY_SHORTS[days[days.length - 1]]}`;
  const shorts = days.map((d) => DAY_SHORTS[d]);
  return `${shorts.slice(0, -1).join(', ')} & ${shorts[shorts.length - 1]}`;
}

/** The weekly items of the active season plus every one-off; nothing from another season. */
export function itemsInSeason(items: ScheduleItemLike[], activeTemplateId: string | null | undefined): ScheduleItemLike[] {
  const templateId = activeTemplateId ? String(activeTemplateId) : null;
  return (items || []).filter((item) => {
    if (!item || item.isDeleted) return false;
    if (item.recurrenceType === 'once') return true;
    return Boolean(templateId) && String(item.templateId || '') === templateId;
  });
}

export function overrideForDate(item: ScheduleItemLike, dateIso: string): ScheduleOverrideLike | null {
  const overrides = Array.isArray(item.overrides) ? item.overrides : [];
  return overrides.find((o) => o && !o.isDeleted && o.date === dateIso) || null;
}

export interface ResolveDayOptions {
  items: ScheduleItemLike[];
  dateIso: string;
  resolver?: ScheduleZmanResolver | null;
  roomNameById?: Record<string, string>;
  /** Drop inactive items (the admin grid keeps them, dimmed, for editing). */
  activeOnly?: boolean;
}

/**
 * Every item that falls on a day, in clock order, with its date-specific
 * change applied and its zman-relative time resolved for that very date.
 */
export function resolveDayOccurrences(options: ResolveDayOptions): ScheduleOccurrence[] {
  const { items, dateIso, resolver, roomNameById = {}, activeOnly = false } = options;
  const dayOfWeek = dateOfIso(dateIso).getDay();
  const occurrences: ScheduleOccurrence[] = [];

  for (const item of items || []) {
    if (!item || item.isDeleted) continue;
    if (activeOnly && item.isActive === false) continue;
    const onDay = item.recurrenceType === 'once'
      ? item.date === dateIso
      : Array.isArray(item.daysOfWeek) && item.daysOfWeek.includes(dayOfWeek);
    if (!onDay) continue;

    const override = overrideForDate(item, dateIso);
    const isCancelled = Boolean(override && override.action === 'cancel');
    const retimed = override && override.action === 'retime' ? override : null;

    const timingMode = retimed ? (retimed.timingMode || 'fixed') : (item.timingMode || 'fixed');
    const fixedTime = retimed ? retimed.fixedTime : item.fixedTime;
    const zmanKey = retimed ? retimed.zmanKey : item.zmanKey;
    const zmanOffsetMinutes = Number((retimed ? retimed.zmanOffsetMinutes : item.zmanOffsetMinutes) ?? 0) || 0;
    const durationMinutes = Math.max(1, Number((retimed && retimed.durationMinutes) || item.durationMinutes || 30) || 30);
    const roomId = (retimed && retimed.roomId) || item.roomId || null;

    let startMinutes: number | null = null;
    let zmanUnresolved = false;
    if (timingMode === 'zman') {
      const resolved = resolver && zmanKey ? resolver.resolve(dateIso, String(zmanKey), zmanOffsetMinutes) : null;
      if (resolved) startMinutes = Math.round(resolved.minutes);
      else zmanUnresolved = true;
    } else {
      startMinutes = clockToMinutes(fixedTime);
    }
    const endMinutes = startMinutes === null ? null : startMinutes + durationMinutes;

    occurrences.push({
      key: `${item.id}@${dateIso}`,
      item,
      override,
      date: dateIso,
      dayOfWeek,
      title: item.title,
      kind: kindOf(item.kind),
      roomId,
      roomName: roomId ? (roomNameById[String(roomId)] || '') : '',
      startMinutes,
      endMinutes,
      durationMinutes,
      timeLabel: startMinutes === null ? (zmanUnresolved ? 'Zman' : '--:--') : formatMinutes(startMinutes),
      rangeLabel: startMinutes === null ? (zmanUnresolved ? 'Needs location' : '--:--') : formatMinutesRange(startMinutes, endMinutes),
      timingSummary: formatTimingSummary({ timingMode, fixedTime, zmanKey, zmanOffsetMinutes }),
      isZman: timingMode === 'zman',
      zmanUnresolved,
      recurrenceLabel: recurrenceLabel(item),
      isOneOff: item.recurrenceType === 'once',
      isOverridden: Boolean(override),
      isCancelled,
      needsVerification: Boolean(item.needsVerification || (override && override.needsVerification)),
      notes: String(item.notes || ''),
      overrideNote: String(override?.note || ''),
      color: item.color || null,
    });
  }

  return occurrences.sort((a, b) => {
    const aStart = a.startMinutes === null ? Number.POSITIVE_INFINITY : a.startMinutes;
    const bStart = b.startMinutes === null ? Number.POSITIVE_INFINITY : b.startMinutes;
    if (aStart !== bStart) return aStart - bStart;
    const aOrder = Number(a.item.order) || 0;
    const bOrder = Number(b.item.order) || 0;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.title.localeCompare(b.title);
  });
}

export interface DisplayRouting {
  roomIds?: string[] | null;
  kinds?: string[] | null;
}

export interface DisplayFilterOptions {
  settings: WeeklyScheduleSettings;
  routing?: DisplayRouting | null;
  /** Minutes past midnight right now; past items are only dropped when known. */
  nowMinutes?: number | null;
  /** The day the screen is on; one-offs and expiries are judged against it. */
  isToday?: boolean;
}

/**
 * What a public screen actually shows of a day: the routed rooms and kinds,
 * nothing cancelled, nothing still awaiting verification while that hold is
 * on, and — on the current day — one-offs that have already ended.
 */
export function filterOccurrencesForDisplay(
  occurrences: ScheduleOccurrence[],
  options: DisplayFilterOptions
): ScheduleOccurrence[] {
  const { settings, routing, nowMinutes, isToday = false } = options;
  const roomIds = (routing?.roomIds || []).map(String);
  const kinds = (routing?.kinds || []).map(String);
  const now = Number.isFinite(nowMinutes as number) ? (nowMinutes as number) : null;

  return occurrences.filter((occurrence) => {
    if (occurrence.isCancelled) return false;
    if (occurrence.item.isActive === false) return false;
    if (settings.holdUnverified && occurrence.needsVerification) return false;
    if (roomIds.length && (!occurrence.roomId || !roomIds.includes(String(occurrence.roomId)))) return false;
    if (kinds.length && !kinds.includes(occurrence.kind)) return false;
    if (isToday && now !== null && occurrence.endMinutes !== null) {
      const ended = occurrence.endMinutes <= now;
      // A one-off vanishes once it is over; a regular item stays on the
      // day's list unless the organization chose to hide past items.
      if (ended && (occurrence.isOneOff || settings.hidePastItems)) return false;
    }
    return true;
  });
}

export interface NowNext {
  current: ScheduleOccurrence | null;
  next: ScheduleOccurrence | null;
  minutesUntilNext: number | null;
}

/** What is on right now and what comes up next, given the clock. */
export function computeNowNext(occurrences: ScheduleOccurrence[], nowMinutes: number): NowNext {
  const timed = occurrences.filter((o) => o.startMinutes !== null && !o.isCancelled);
  const current = timed.find((o) => (o.startMinutes as number) <= nowMinutes && (o.endMinutes as number) > nowMinutes) || null;
  const next = timed.find((o) => (o.startMinutes as number) > nowMinutes) || null;
  return {
    current,
    next,
    minutesUntilNext: next ? (next.startMinutes as number) - nowMinutes : null,
  };
}

/** "Mincha begins in 12 minutes", "Maariv begins in 1 hour 5 minutes", "Shacharit is starting now". */
export function formatCountdown(title: string, minutesUntil: number | null): string {
  if (minutesUntil === null || !Number.isFinite(minutesUntil)) return '';
  if (minutesUntil <= 0) return `${title} is starting now`;
  const hours = Math.floor(minutesUntil / 60);
  const minutes = minutesUntil % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (minutes || !hours) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  return `${title} begins in ${parts.join(' ')}`;
}

/** Colours the admin grid draws an item in: soft fill, border and text. */
export function kindPalette(kind: unknown, override?: string | null): { fill: string; border: string; text: string; dot: string } {
  if (override && HEX_RE.test(override)) {
    return { fill: `${override}22`, border: `${override}66`, text: '#1F2330', dot: override };
  }
  switch (kindOf(kind)) {
    case 'minyan':
      return { fill: '#E0E3FE', border: '#C7D2FE', text: '#1F2330', dot: '#6366F1' };
    case 'shiur':
      return { fill: '#D1FAE5', border: '#A7F3D0', text: '#1F2330', dot: '#10B981' };
    case 'event':
      return { fill: '#FEF3C7', border: '#FDE68A', text: '#1F2330', dot: '#F59E0B' };
    default:
      return { fill: '#F4F5F8', border: '#E6E8EE', text: '#1F2330', dot: '#8A91A3' };
  }
}

/** The hour band a grid needs to show every occurrence, padded, at least 6 AM – 11 PM. */
export function hourRangeFor(occurrences: ScheduleOccurrence[], fallback: [number, number] = [6, 23]): [number, number] {
  let min = fallback[0] * 60;
  let max = fallback[1] * 60;
  for (const occurrence of occurrences) {
    if (occurrence.startMinutes === null) continue;
    min = Math.min(min, occurrence.startMinutes - 30);
    max = Math.max(max, (occurrence.endMinutes || occurrence.startMinutes) + 30);
  }
  return [Math.max(0, Math.floor(min / 60)), Math.min(24, Math.ceil(max / 60))];
}

/** A day from a Date in the admin's own clock, for "today" markers and defaults. */
export const todayIsoLocal = (): string => isoOfDate(new Date());

/** "YYYY-MM-DD" of any date-ish input, or '' */
export const toScheduleIso = (value: unknown): string => toWallClockDateInputValue(value);

/* -------------------------------- templates ------------------------------ */

export type WeeklyScheduleDesignRegion = 'heading' | 'event';

// The board's two templates. The Design tab edits one at a time, each in an
// editor of its own.
export const WEEKLY_SCHEDULE_DESIGN_REGIONS: Array<{ value: WeeklyScheduleDesignRegion; label: string; hint: string }> = [
  { value: 'heading', label: 'Heading', hint: 'Printed once at the top of each day.' },
  { value: 'event', label: 'Event', hint: 'Printed for every minyan, shiur and event.' },
];

export interface WeeklyScheduleTemplateVariable {
  value: string;
  label: string;
}

// What the heading can print. Filled once per day.
export const WEEKLY_SCHEDULE_HEADING_VARIABLES: WeeklyScheduleTemplateVariable[] = [
  { value: 'dayOfWeek', label: 'English day of week' },
  { value: 'monthDay', label: 'English month + day' },
  { value: 'year', label: 'English year' },
  { value: 'hebrewDayOfWeek', label: 'Hebrew day of week' },
  { value: 'hebrewMonthDay', label: 'Hebrew month + day' },
  { value: 'hebrewYear', label: 'Hebrew year' },
  { value: 'parsha', label: 'Parsha' },
  { value: 'holidays', label: 'Holidays' },
  { value: 'dayNotes', label: 'Day notes' },
  { value: 'now', label: 'Now (on at the moment)' },
  { value: 'next', label: 'Next (coming up)' },
  { value: 'countdown', label: 'Countdown' },
];

// What the event can print. Filled once per item, for its own date.
export const WEEKLY_SCHEDULE_EVENT_VARIABLES: WeeklyScheduleTemplateVariable[] = [
  { value: 'title', label: 'Title' },
  { value: 'time', label: 'Start time' },
  { value: 'endTime', label: 'End time' },
  { value: 'timeRange', label: 'Time range' },
  { value: 'timing', label: 'Time rule' },
  { value: 'room', label: 'Room' },
  { value: 'notes', label: 'Notes' },
  { value: 'type', label: 'Type' },
  { value: 'recurrence', label: 'Repeats' },
  { value: 'status', label: 'Now / Next' },
];

export type WeeklyScheduleTemplateValue = string | string[] | null | undefined;

const TOKEN_PATTERN = /\{(\w+)\}/g;
const HAS_TOKEN = /\{\w+\}/;

const valueLines = (value: WeeklyScheduleTemplateValue): string[] => {
  if (value === null || value === undefined) return [];
  const lines = Array.isArray(value) ? value : String(value).split(/\r\n|\r|\n/);
  return lines.map((line) => String(line ?? '')).filter((line, index, all) => line !== '' || all.length > 1);
};

/**
 * Fill a template's `{variables}`.
 *
 * Substituted on text nodes, never by string replacement, so a value — a
 * room name, a note — can only ever be text: it cannot open a tag. Each value
 * becomes its own <span>, which is what lets a "split row" push two values to
 * opposite ends; a value with several lines gets a <br> between them. A value
 * that is empty leaves nothing behind, so a line holding only that variable
 * folds away. Blank lines the author typed are marked first and keep their
 * height (see utils/authored_blank_lines.ts). A `{word}` that is not a
 * variable is left exactly as typed.
 */
export function renderWeeklyScheduleTemplate(
  html: string,
  values: Record<string, WeeklyScheduleTemplateValue>
): string {
  if (!html || !html.trim() || typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(`<div id="ws-template">${html}</div>`, 'text/html');
  const root = doc.getElementById('ws-template');
  if (!root) return '';

  markAuthoredBlankLines(root);

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (node.nodeValue && HAS_TOKEN.test(node.nodeValue)) targets.push(node);
  }

  targets.forEach((node) => {
    const text = node.nodeValue || '';
    const fragment = doc.createDocumentFragment();
    let cursor = 0;
    text.replace(TOKEN_PATTERN, (match: string, key: string, offset: number) => {
      if (offset > cursor) fragment.appendChild(doc.createTextNode(text.slice(cursor, offset)));
      cursor = offset + match.length;
      if (!Object.prototype.hasOwnProperty.call(values, key)) {
        fragment.appendChild(doc.createTextNode(match));
        return match;
      }
      const lines = valueLines(values[key]);
      if (lines.length) {
        const span = doc.createElement('span');
        span.className = 'ws-value';
        lines.forEach((line, index) => {
          if (index) span.appendChild(doc.createElement('br'));
          span.appendChild(doc.createTextNode(line));
        });
        fragment.appendChild(span);
      }
      return match;
    });
    if (cursor < text.length) fragment.appendChild(doc.createTextNode(text.slice(cursor)));
    node.parentNode?.replaceChild(fragment, node);
  });

  return root.innerHTML;
}

const occurrenceTimeText = (occurrence: ScheduleOccurrence): string =>
  occurrence.startMinutes !== null ? formatMinutes(occurrence.startMinutes) : occurrence.timingSummary;

export interface HeadingTemplateOptions {
  headerPreset: WeeklyScheduleHeaderPreset;
  /** The day's own notes, in order. */
  notes: string[];
  /** What is on and next — read on the current day only. */
  nowNext?: NowNext | null;
  showNowNext: boolean;
  showCountdown: boolean;
}

/** The heading's values for one day. */
export function buildHeadingTemplateValues(
  day: ScheduleDay,
  options: HeadingTemplateOptions
): Record<string, WeeklyScheduleTemplateValue> {
  const live = day.isToday && options.showNowNext ? options.nowNext || null : null;
  const current = live?.current || null;
  const next = live?.next || null;
  const holidays = day.markers
    .filter((marker) => marker.tone !== 'shabbat' && marker.tone !== 'parsha')
    .map((marker) => marker.label);
  return {
    dayOfWeek: day.dayLabel,
    monthDay: day.monthDayLong,
    year: String(day.year),
    hebrewDayOfWeek: day.hebrewWeekdayHe,
    hebrewMonthDay: day.hebrewLabelHe,
    hebrewYear: day.hebrewYearHe,
    // No longer in the Variable menu, but a design saved while they were
    // still prints them.
    header: formatScheduleHeader(day, options.headerPreset),
    dayName: day.dayLabel,
    date: day.monthDay,
    hebrewDate: day.hebrewFull,
    hebrewDateLine: options.headerPreset === 'full' ? '' : day.hebrewFull,
    hebrewDateHe: day.hebrewFullHe,
    parsha: day.parsha ? `Parshat ${day.parsha}` : '',
    holidays: holidays.join(' · '),
    dayNotes: (options.notes || []).map((note) => String(note || '').trim()).filter(Boolean),
    now: current ? `${current.title} · ${occurrenceTimeText(current)}` : '',
    next: next ? `${next.title} · ${occurrenceTimeText(next)}` : '',
    countdown: next && options.showCountdown ? formatCountdown(next.title, live?.minutesUntilNext ?? null) : '',
  };
}

export type WeeklyScheduleEventStatus = '' | 'Now' | 'Next';

/** An event's values, for its own date. */
export function buildEventTemplateValues(
  occurrence: ScheduleOccurrence,
  status: WeeklyScheduleEventStatus = ''
): Record<string, WeeklyScheduleTemplateValue> {
  const timed = occurrence.startMinutes !== null;
  return {
    title: occurrence.title,
    time: occurrenceTimeText(occurrence),
    endTime: timed && occurrence.endMinutes !== null ? formatMinutes(occurrence.endMinutes) : '',
    timeRange: timed ? formatMinutesRange(occurrence.startMinutes, occurrence.endMinutes) : occurrence.timingSummary,
    timing: occurrence.timingSummary,
    room: occurrence.roomName,
    notes: occurrence.overrideNote || occurrence.notes,
    type: kindLabel(occurrence.kind),
    recurrence: occurrence.recurrenceLabel,
    status,
  };
}
